import React, { useState } from 'react';
import { X, Bell, Star, AlertCircle, Check } from 'lucide-react';
import { useCalendarSync } from '../../hooks/useCalendarSync';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { UpgradePrompt } from '../Premium/UpgradePrompt';
import { OAuthConnect } from './OAuthConnect';
import { oauthProviders } from '../../lib/oauth';

interface CalendarSyncProps {
  onClose: () => void;
}

export function CalendarSync({ onClose }: CalendarSyncProps) {
  const { hasFeature, showUpgradePrompt } = useSubscription();
  const { settings, updateSettings } = useCalendarSync();
  const [selectedProvider, setSelectedProvider] = useState<'google' | 'outlook' | 'apple' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isPremium = hasFeature('calendar_sync');

  const providers = [
    { id: 'google', name: 'Google Calendar' }
  ];

  const alertOptions = [
    { value: 5, label: '5 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' }
  ];

  const handleSave = async () => {
    if (!selectedProvider) return;

    // For Google Calendar, initiate OAuth flow
    if (selectedProvider === 'google') {
      try {
        oauthProviders.google.authorize();
        return;
      } catch (error) {
        console.error('OAuth error:', error);
        setError('Failed to connect to Google Calendar');
      }
      return;
    }

    try {
      await updateSettings(selectedProvider, {
        sync_enabled: true,
        alert_minutes_before: [15, 30]
      });
      onClose();
    } catch (error) {
      console.error('Error saving calendar sync settings:', error);
      setError('Failed to save calendar settings');
    }
  };

  if (!isPremium) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">Calendar Sync</h3>
            <button onClick={onClose} className="p-2 hover:bg-black/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
          <UpgradePrompt
            feature="Calendar Sync"
            description="Sync your tasks and events with your favorite calendar apps and get timely reminders."
            onUpgrade={() => showUpgradePrompt('calendar_sync')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Calendar Sync</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h4 className="text-sm text-gray-400 mb-2">Select Calendar</h4>
            <div className="space-y-2">
              {providers.map((provider) => {
                const isConnected = settings.some(s => 
                  s.provider === provider.id && s.sync_enabled
                );

                return (
                  <button
                    key={provider.id}
                    onClick={() => setSelectedProvider(provider.id as any)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                      isConnected
                        ? 'bg-[#bbdde5] text-black'
                        : selectedProvider === provider.id
                        ? 'bg-[#bbdde5] text-black'
                        : 'bg-black/20 hover:bg-black/30'
                    }`}
                  >
                    <span>{provider.name}</span>
                    {isConnected && <Check className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-4">
            <OAuthConnect
              onSuccess={() => {
                setError(null);
                // Refresh settings after successful connection
                updateSettings();
              }}
              onError={setError}
            />

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-500 rounded-xl">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {selectedProvider && (
              <>
                <div>
                  <h4 className="text-sm text-gray-400 mb-2">Reminder Settings</h4>
                  <div className="space-y-2">
                    {alertOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-3 p-3 bg-black/20 rounded-xl"
                      >
                        <input
                          type="checkbox"
                          className="rounded bg-black/20"
                          defaultChecked={option.value === 15 || option.value === 30}
                        />
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-gray-400" />
                          <span>{option.label} before</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSave}
                  className="w-full bg-[#bbdde5] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
                >
                  Save Settings
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
